// Accumulates a live session's telemetry frames into the high-fidelity markers
// the recovery engine needs at session close: peak power, peak velocity, total
// powered duration, HR intervals, distance and calories.

import type { MachineKind, TelemetryFrame } from './types'

export type SessionMarkers = {
  machineKind: MachineKind
  durationSeconds: number
  peakPowerWatts: number
  avgPowerWatts: number
  peakVelocityMs: number
  maxHeartRate: number
  avgHeartRate: number
  hrIntervals: { t: number; hr: number }[]
  totalDistanceM: number
  totalCalories: number
  sampleCount: number
}

export class SessionAggregator {
  private startedAt = Date.now()
  private machineKind: MachineKind = 'unknown'
  private powerSamples: number[] = []
  private hrSamples: { t: number; hr: number }[] = []
  private peakPower = 0
  private peakVelocity = 0
  private maxDistance = 0
  private maxCalories = 0
  private sampleCount = 0

  reset() {
    this.startedAt = Date.now()
    this.machineKind = 'unknown'
    this.powerSamples = []
    this.hrSamples = []
    this.peakPower = 0
    this.peakVelocity = 0
    this.maxDistance = 0
    this.maxCalories = 0
    this.sampleCount = 0
  }

  ingest(frame: TelemetryFrame) {
    this.sampleCount += 1
    if (frame.machineKind !== 'unknown') this.machineKind = frame.machineKind

    const { power_watts, mean_velocity_ms, peak_velocity_ms, heart_rate_bpm, distance_m, calories_kcal } =
      frame.metrics

    if (power_watts != null && power_watts > 0) {
      this.powerSamples.push(power_watts)
      if (power_watts > this.peakPower) this.peakPower = power_watts
    }
    const velocity = peak_velocity_ms ?? mean_velocity_ms
    if (velocity != null && velocity > this.peakVelocity) this.peakVelocity = velocity
    if (heart_rate_bpm != null && heart_rate_bpm > 0) {
      this.hrSamples.push({ t: frame.timestamp, hr: heart_rate_bpm })
    }
    // Distance / calories are cumulative counters — keep the max seen.
    if (distance_m != null && distance_m > this.maxDistance) this.maxDistance = distance_m
    if (calories_kcal != null && calories_kcal > this.maxCalories) this.maxCalories = calories_kcal
  }

  finalize(): SessionMarkers {
    const avgPower = this.powerSamples.length
      ? this.powerSamples.reduce((a, b) => a + b, 0) / this.powerSamples.length
      : 0
    const hrValues = this.hrSamples.map((s) => s.hr)
    const avgHr = hrValues.length ? hrValues.reduce((a, b) => a + b, 0) / hrValues.length : 0
    const maxHr = hrValues.length ? Math.max(...hrValues) : 0

    return {
      machineKind: this.machineKind,
      durationSeconds: Math.round((Date.now() - this.startedAt) / 1000),
      peakPowerWatts: Math.round(this.peakPower * 10) / 10,
      avgPowerWatts: Math.round(avgPower * 10) / 10,
      peakVelocityMs: Math.round(this.peakVelocity * 100) / 100,
      maxHeartRate: maxHr,
      avgHeartRate: Math.round(avgHr),
      hrIntervals: this.hrSamples,
      totalDistanceM: Math.round(this.maxDistance),
      totalCalories: Math.round(this.maxCalories),
      sampleCount: this.sampleCount,
    }
  }
}
